import { cn } from '@/core/utils/cn';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';

type AlertVariant = 'info' | 'success' | 'warning' | 'error';

interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  children: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const variantStyles: Record<AlertVariant, { bg: string; border: string; icon: React.ReactNode }> = {
  info: { bg: 'bg-hms-blue/5', border: 'border-hms-blue/20', icon: <Info className="w-4 h-4 text-hms-blue" /> },
  success: { bg: 'bg-hms-green/5', border: 'border-hms-green/20', icon: <CheckCircle className="w-4 h-4 text-hms-green" /> },
  warning: { bg: 'bg-hms-gold/5', border: 'border-hms-gold/20', icon: <AlertTriangle className="w-4 h-4 text-hms-gold" /> },
  error: { bg: 'bg-hms-red/5', border: 'border-hms-red/20', icon: <AlertCircle className="w-4 h-4 text-hms-red" /> },
};

export function Alert({ variant = 'info', title, children, dismissible, onDismiss, className }: AlertProps) {
  const styles = variantStyles[variant];
  return (
    <div className={cn('flex gap-3 p-4 rounded-xl border', styles.bg, styles.border, className)}>
      <div className="mt-0.5 flex-shrink-0">{styles.icon}</div>
      <div className="flex-1 min-w-0">
        {title && <p className="text-[13px] font-semibold text-hms-text mb-0.5">{title}</p>}
        <div className="text-[12px] text-hms-textSub">{children}</div>
      </div>
      {dismissible && (
        <button id="shared-alert-dismiss" onClick={onDismiss} className="text-hms-textDim hover:text-hms-text flex-shrink-0">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
